import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify, { MESSAGES } from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import {
  FileField,
  FormField,
  FormGrid,
  SelectField,
  TextAreaField,
  TextField,
  focusFirstError,
  hasErrors,
  required,
  validateForm,
} from '../../Components/Form';
import { formatFileSize } from '../../utils/format';
import StatusBadge from '../../Components/Common/StatusBadge';

const DOCUMENT_UPLOAD_SCHEMA = {
  documentName: { label: 'Document name', rules: [required()] },
  documentType: { label: 'Document type', rules: [required()] },
};
const DOCUMENT_UPLOAD_ORDER = ['documentName', 'documentType', 'fileUpload'];

const DocumentUploadMasterList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalDocumentUpload, setModalDocumentUpload] = useState(false);
  const [errors, setErrors] = useState({});
  const [documentUploadFormData, setDocumentUploadFormData] = useState({
    documentName: null,
    description: null,
    documentType: 1,
    id: null,
    path: null,
  });

  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState('all');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  // Document type options for dropdown
  const documentTypeOptions = [
    { value: 1, label: 'Procurement' },
    { value: 2, label: 'HPS' },
  ];

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(
          `${api.API_URL}/document-upload/${invoice.id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        notify.success('Document Upload Master deleted successfully.');
        setDeleteModal(false);
        setInvoice(null);
        fetchData(
          currentPage,
          rowsPerPage,
          token,
          search,
          selectedDocumentType
        );
      } catch (err) {
        notify.apiError(err, 'delete document upload master');
      }
    }
  };

  const deleteMultiple = () => {
    // Implement multiple delete functionality
    // This would typically involve deleting selected items
    // For now, just close the modal
    setDeleteModalMulti(false);
  };

  const getDocumentTypeLabel = type => {
    const option = documentTypeOptions.find(opt => opt.value === type);
    return option ? option.label : 'Unknown';
  };

  // const handleViewPath = filePath => {
  //   if (filePath) {
  //     window.open(filePath, '_blank', 'noopener,noreferrer');
  //   } else {
  //     toast.info('No file available to view');
  //   }
  // };

  const handleViewPath = filePath => {
    if (!filePath) {
      notify.info('There is no file available for this document.');
      return;
    }
    const link = document.createElement('a');
    link.href = filePath;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      id: 'documentName',
      name: 'Document Name',
      selector: row => row.documentName,
      sortable: true,
      width: '30%',
      cell: row => (
        <span className='to-title' title={String(row.documentName ?? '')}>
          {row.documentName ?? '-'}
        </span>
      ),
    },
    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      sortable: true,
      width: '40%',
      wrap: true,
      cell: row => (
        <span title={String(row.description ?? '')}>
          {row.description ?? '-'}
        </span>
      ),
    },
    {
      id: 'documentType',
      name: 'Document Type',
      selector: row => row.documentType,
      sortable: true,
      width: '15%',
      cell: row => (
        <StatusBadge
          tone={row.documentType === 1 ? 'info' : 'success'}
          label={getDocumentTypeLabel(row.documentType)}
        />
      ),
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      width: '15%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-eye-line'
            label='View'
            onClick={() => handleViewPath(row.path)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row.id)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Document Upload Master';

  const handleChange = e => {
    const { name, value } = e.target;
    setDocumentUploadFormData({
      ...documentUploadFormData,
      [name]: name === 'documentType' ? parseInt(value) : value,
    });
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const fetchData = useCallback(
    async (currentPage, rowsPerPage, token, search, documentType) => {
      const reqId = ++requestRef.current;
      setLoading(true);
      try {
        // Use the single GET endpoint for all data
        const apiUrl = `${api.API_URL}/document-upload?PageNumber=${currentPage}&PageSize=${rowsPerPage}${search ? `&Search=${encodeURIComponent(search)}` : ''}${documentType !== 'all' ? `&DocumentType=${documentType}` : ''}`;

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (reqId !== requestRef.current) return; // stale response

        // Handle different possible response structures
        const items =
          result?.data?.items || result?.items || result?.data || result || [];
        const totalItems =
          result?.data?.totalItems || result?.totalItems || items.length;

        setData({
          items: Array.isArray(items) ? items : [],
        });
        setTotalRows(totalItems);
      } catch (error) {
        if (reqId !== requestRef.current) return;
        notify.apiError(error, 'load data');
        setData({ items: [] });
        setTotalRows(0);
      } finally {
        if (reqId === requestRef.current) setLoading(false);
      }
    },
    []
  );

  const handleDocumentTypeFilterChange = value => {
    setSelectedDocumentType(value);
    setCurrentPage(1);
  };

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const clearFilter = () => {
    setFilterText('');
    setSearch('');
    setCurrentPage(1);
    setSelectedDocumentType('all');
  };

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterChange({ target: { value: '' } }),
    },
    selectedDocumentType !== 'all' && {
      key: 'type',
      label: 'Document Type',
      value: getDocumentTypeLabel(parseInt(selectedDocumentType)),
      onRemove: () => handleDocumentTypeFilterChange('all'),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token, search, selectedDocumentType);
  }, [
    currentPage,
    rowsPerPage,
    token,
    search,
    selectedDocumentType,
    fetchData,
  ]);

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setDocumentUploadFormData({
      documentName: null,
      description: null,
      documentType: 1,
      id: null,
      path: null,
    });
    setSelectedFile(null);
    setErrors({});
    documentUploadModal();
  };

  const handleUpdateModal = async (mid = null) => {
    setErrors({});
    await fetchDocumentUploadDetail(mid);
    documentUploadModal();
  };

  const documentUploadModal = () => {
    setModalDocumentUpload(!modalDocumentUpload);
  };

  const handleFileChange = e => {
    setSelectedFile(e.target.files[0] || null);
    setErrors(prev => ({ ...prev, fileUpload: '' }));
  };

  const handleSaveDocumentUpload = async () => {
    const nextErrors = validateForm(
      documentUploadFormData,
      DOCUMENT_UPLOAD_SCHEMA
    );
    // File wajib saat menambah; saat edit boleh memakai file yang sudah ada.
    if (!selectedFile && !documentUploadFormData.path) {
      nextErrors.fileUpload = 'File attachment is required';
    }
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) {
      focusFirstError(nextErrors, DOCUMENT_UPLOAD_ORDER);
      return;
    }
    await (documentUploadFormData.id !== null
      ? handleUpdDataDocumentUpload(documentUploadFormData.id)
      : handleAddDataDocumentUpload());
    setModalDocumentUpload(false);
  };

  const handleAddDataDocumentUpload = async () => {
    try {
      const formData = new FormData();
      formData.append('documentName', documentUploadFormData.documentName);
      formData.append('description', documentUploadFormData.description);
      formData.append('documentType', documentUploadFormData.documentType);
      if (selectedFile) {
        formData.append('File', selectedFile);
      }
      await axios.post(`${api.API_URL}/document-upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success('Document Upload Master added successfully.');
      setSelectedFile(null);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search, selectedDocumentType);
    }
  };

  const fetchDocumentUploadDetail = async mid => {
    try {
      setSelectedFile(null);
      const response = await axios.get(
        `${api.API_URL}/document-upload/${mid}`,
        {
          withXSRFToken: true,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: `application/json`,
          },
        }
      );
      const data = response.data || response;
      setDocumentUploadFormData({
        documentName: data.documentName || null,
        description: data.description || null,
        documentType: data.documentType || 1,
        id: data.id || null,
        path: data.path || data.filePath || null,
      });
    } catch (error) {
      notify.apiError(error, 'fetch document upload detail');
    }
  };

  const handleUpdDataDocumentUpload = async mid => {
    try {
      const formData = new FormData();
      formData.append('documentName', documentUploadFormData.documentName);
      formData.append('description', documentUploadFormData.description);
      formData.append('documentType', documentUploadFormData.documentType);
      if (selectedFile) {
        formData.append('File', selectedFile);
      }
      await axios.put(`${api.API_URL}/document-upload/${mid}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success(MESSAGES.updated);
      setSelectedFile(null);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search, selectedDocumentType);
    }
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this document upload?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.documentName}
          confirmLabel='Delete document upload'
        />
        <DeleteModal
          show={deleteModalMulti}
          onDeleteClick={() => {
            deleteMultiple();
            setDeleteModalMulti(false);
          }}
          onCloseClick={() => setDeleteModalMulti(false)}
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{
                      label: 'Document Upload Master',
                      to: '/apps-document-upload-master/view',
                    }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-file-upload-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Document Upload Master</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the master documents used in procurement and
                        HPS.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(
                            currentPage,
                            rowsPerPage,
                            token,
                            search,
                            selectedDocumentType
                          )
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Document Upload Master
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search document upload masters...'
                      ariaLabel='Search document upload masters'
                    />
                    <ListFilterSelect
                      label='Document Type'
                      value={selectedDocumentType}
                      onChange={handleDocumentTypeFilterChange}
                      emptyValue='all'
                      allLabel='All Document Types'
                      options={documentTypeOptions}
                      ariaLabel='Filter by document type'
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} document upload masters`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='documentName'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No document upload master found'
                          emptyHint='No document upload masters have been defined yet. Entries added here define the documents that can be uploaded.'
                          filteredHint='No document upload master matches the current filters.'
                          hasFilters={hasFilters}
                          onClear={clearFilter}
                          action={{
                            label: 'Create New Document Upload Master',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalDocumentUpload}
            role='dialog'
            autoFocus={true}
            centered
            id='addDocumentUploadMasterModal'
            toggle={documentUploadModal}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setErrors({});
                setModalDocumentUpload(!modalDocumentUpload);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {documentUploadFormData.id !== null
                  ? 'Edit Document Upload Master'
                  : 'Add Document Upload Master'}
              </span>
            </ModalHeader>

            <ModalBody>
              <FormGrid columns={1}>
                <TextField
                  label='Document name'
                  name='documentName'
                  placeholder='e.g. Company registration certificate'
                  value={documentUploadFormData.documentName}
                  onChange={handleChange}
                  required
                  error={errors.documentName}
                />
                <TextAreaField
                  label='Description'
                  name='description'
                  value={documentUploadFormData.description}
                  onChange={handleChange}
                />
                <SelectField
                  label='Document type'
                  name='documentType'
                  clearable={false}
                  options={documentTypeOptions}
                  value={documentUploadFormData.documentType || 1}
                  onChange={value => {
                    setDocumentUploadFormData(prev => ({
                      ...prev,
                      documentType: value ?? 1,
                    }));
                    setErrors(prev => ({ ...prev, documentType: '' }));
                  }}
                  required
                  error={errors.documentType}
                />
                {documentUploadFormData.id !== null &&
                  documentUploadFormData.path && (
                    <FormField label='Current file' name='currentFile' group>
                      {() => (
                        <div className='d-flex align-items-center gap-2 p-2 border rounded bg-light'>
                          <i className='ri-file-text-line fs-16'></i>
                          <span className='text-truncate flex-grow-1 small'>
                            {documentUploadFormData.path.split('/').pop() ||
                              documentUploadFormData.path}
                          </span>
                          <ActionButton
                            variant='secondary'
                            size='sm'
                            icon='ri-eye-line'
                            onClick={() =>
                              handleViewPath(documentUploadFormData.path)
                            }
                          >
                            View
                          </ActionButton>
                        </div>
                      )}
                    </FormField>
                  )}
                <FileField
                  label='File attachment'
                  name='fileUpload'
                  inputRef={fileInputRef}
                  onChange={handleFileChange}
                  required
                  error={errors.fileUpload}
                  hint={
                    selectedFile
                      ? `${selectedFile.name} (${formatFileSize(selectedFile.size)})`
                      : undefined
                  }
                />
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => {
                  setErrors({});
                  setModalDocumentUpload(!modalDocumentUpload);
                }}
              >
                Close
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={handleSaveDocumentUpload}
              >
                {documentUploadFormData.id !== null ? 'Update' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DocumentUploadMasterList;
